import { useEffect, useState, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { GridColDef, GridRowsProp, GridRowId } from "@mui/x-data-grid"
import { format } from "date-fns"
import classNames from "classnames"

import Table from "@/components/table/Table"
import Icon from "@/components/icon/Icon"
import { Notice } from "@/types/notice"
import {
  useCreateNotificationReadHistoryMutation,
  useNotificationsQuery,
} from "@/hooks/api/useNoticeQuery"
import { PAGE_SIZE } from "@/constants/pagination"
import { YEAR_TO_DAY_FORMAT } from "@/constants/regex"
import Pagination from "@/components/table/Pagination"

import "./noticeTable.scss"

export default function NoticeTable({
  searchValue,
  page,
  setPage,
}: {
  searchValue: string
  page: number
  setPage: (page: number) => void
}) {
  const { t, i18n } = useTranslation()
  const [rows, setRows] = useState<GridRowsProp<Notice>>([])
  const [paginationModel, setPaginationModel] = useState({ page, pageSize: 10 })
  const [searchKey, setSearchKey] = useState("")
  const [expandedRowId, setExpandedRowId] = useState<Set<GridRowId>>(new Set())

  const readHistoryMutation = useCreateNotificationReadHistoryMutation()

  const { data, isLoading } = useNotificationsQuery(
    {
      language: i18n.language,
      name: searchKey,
      page: page + 1,
      size: PAGE_SIZE,
    },
    [searchKey, String(page + 1)],
  )

  useEffect(() => {
    if (searchValue.length < 2) {
      setSearchKey("")
    } else {
      setPage(0)
      setSearchKey(searchValue)
      setExpandedRowId(new Set()) // 검색 시 확장된 row 초기화
    }
  }, [searchValue])

  useEffect(() => {
    setPaginationModel({ page, pageSize: PAGE_SIZE })
    setExpandedRowId(new Set()) // 페이지 변경 시 확장된 row 초기화
  }, [page])

  useEffect(() => {
    if (!data?.content) {
      return
    }
    setRows(
      data.content.map((notice) => ({
        id: notice.idfNotice,
        recipient: notice.nameRecipient,
        date: notice.dateCreated,
        ...notice,
      })),
    )
  }, [data])

  const columns: GridColDef<Notice>[] = [
    {
      field: "recipient",
      renderHeader: () => t("recipient"),
      width: 220,
      sortable: false,
    },
    {
      field: "title",
      renderHeader: () => t("title"),
      renderCell: (params) => {
        const isSelected = params.api.getSelectedRows().keys().next().value === params.id

        return (
          <>
            <p className={classNames("notice-title", { open: isSelected })}>{params.value}</p>
            {!params.row.beRead && <Icon type="unread-dot" />}
          </>
        )
      },
      maxWidth: 750,
      flex: 1,
      sortable: false,
    },
    {
      field: "date",
      renderHeader: () => t("date"),
      renderCell: (params) => format(params.value as Date, YEAR_TO_DAY_FORMAT),
      width: 180,
      sortable: false,
    },
  ]

  const handleDetailPanelExpandedRowIdsChange = useCallback((newIds: Set<GridRowId>) => {
    if (newIds.size > 1) {
      const newSet = new Set<GridRowId>()
      const newIdsArray = Array.from(newIds)
      newSet.add(newIdsArray[newIdsArray.length - 1])
      setExpandedRowId(newSet)
    } else {
      setExpandedRowId(newIds)
    }
  }, [])

  return (
    <Table
      columns={columns}
      rows={rows}
      getRowId={(row) => `${row.idfNotice}-${row.nameRecipient}`}
      renderDetails={(row) => <NoticeTableDetail data={row} />}
      onRowClick={async (params) => {
        if (!params.row.beRead) {
          try {
            await readHistoryMutation.mutateAsync({
              idfNotice: params.row.idfNotice,
              isTeam: params.row.isTeam,
            })
          } catch (error) {
            console.error("Error creating read history:", error)
          }
        }
      }}
      detailPanelExpandedRowIds={expandedRowId}
      onDetailPanelExpandedRowIdsChange={handleDetailPanelExpandedRowIdsChange}
      disableRowSelectionOnClick
      disableMultipleRowSelection
      disableColumnResize
      disableVirtualization
      pagination
      paginationMode="server"
      rowCount={data?.totalElements || 0}
      paginationModel={paginationModel}
      onPaginationModelChange={(model) => {
        if (!isLoading) {
          setPaginationModel(model)
          setPage(model.page)
        }
      }}
      loading={isLoading}
      slots={{
        pagination: () => <Pagination />,
      }}
      autoHeight // deprecated 해결 요망
    />
  )
}

function NoticeTableDetail({ data }: { data: Record<string, any> }) {
  return (
    <div className="NoticeTableDetailCont">
      <div
        className="notice-table-detail-content"
        dangerouslySetInnerHTML={{ __html: data.content }}
      />
    </div>
  )
}
